import * as React from 'react'
import BottomSheet from 'Components/BottomSheet'
import Icon from 'Components/Icon'
import { useTranslation } from 'react-i18next'
import { format } from 'react-string-format'

type SelectOption = {
  value: number | string
  label: string
  icon?: string
}

interface BottomSheetDropdownProps {
  id?: string
  label: string
  value: SelectOption | null
  onChange: (val: SelectOption | null) => void
  options?: SelectOption[]
  url?: string
  mapper?: (data: any) => SelectOption[]
  withSearch?: boolean
  disabled?: boolean
  rules?: any
  errors?: any
  onInputChange?: (val: string) => void
  contentClassName?: string
}

const BottomSheetDropdown = ({
  id,
  label,
  value,
  onChange,
  options: propOptions,
  url,
  mapper,
  withSearch = true,
  disabled,
  rules,
  errors,
  onInputChange,
  contentClassName = '',
}: BottomSheetDropdownProps) => {
  const [t] = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [options, setOptions] = React.useState<SelectOption[]>(propOptions || [])
  const [filter, setFilter] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  // 🔹 بارگذاری داده‌ها از URL (مشابه ساختار دوم)
  React.useEffect(() => {
    if (!url || url.includes('undefined') || url.endsWith('?')) return

    const loadData = async () => {
      setLoading(true)
      try {
        const res = await fetch(url)
        const data = await res.json()
        const mappedData = mapper
          ? mapper(data)
          : data.map((item: any) => ({ value: item.id, label: item.title }))
        setOptions(mappedData)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [url])

  // آپدیت آپشن‌ها اگر به صورت مستقیم تغییر کنند
  React.useEffect(() => {
    if (propOptions) setOptions(propOptions)
  }, [propOptions])

  const handleSelect = (option: SelectOption) => {
    onChange(option)
    setOpen(false)
    setFilter('')
  }

  return (
    <React.Fragment>
      {/* Input Field با استایل Floating Label */}
      <div
        className={`group form-floating w-full relative ${errors ? ` validation-error` : ``} ${contentClassName}`}
      >
        <input
          id={id}
          type="text"
          readOnly
          className="form-select peer cursor-pointer"
          placeholder=""
          value={value?.label || ''}
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
        />

        <label
          htmlFor={id}
          className="peer-focus:text-xs peer-focus:-top-[8px] peer-focus:text-gray-800 dark:peer-focus:text-white peer-placeholder-shown:text-lg peer-placeholder-shown:top-3"
        >
          <span className="relative">
            {label}
            {rules?.required && <span style={{ color: '#d84b4b' }}>*</span>}
          </span>
        </label>

        {/* نمایش خطا مشابه کامپوننت دوم */}
        {errors && errors.type === 'required' && (
          <div className="text-error text-xs mt-1 hidden group-[.validation-error]:block">
            {errors.message || format(t('VALIDATION_ERROR_REQUIRED'), label)}
          </div>
        )}
      </div>

      {/* BottomSheet با ظاهر کامپوننت دوم */}
      <BottomSheet visible={open} onCancel={() => setOpen(false)} title={label}>
        {/* Handle Bar بالای شیت */}
        <div
          style={{
            width: 50,
            height: 6,
            backgroundColor: '#ccc',
            borderRadius: 3,
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />

        <div className="overflow-auto px-6 pb-6">
          <div className="text-black text-sm mb-4">{`${label} را وارد کنید.`}</div>

          {/* فیلد جستجو با استایل کامپوننت دوم */}
          {withSearch && (
            <div className="group form-floating w-full relative mb-4">
              <input
                id="searchInput"
                type="text"
                className="form-text form-sm peer pl-9"
                placeholder=""
                value={filter}
                onChange={e => {
                  setFilter(e.target.value)
                  onInputChange?.(e.target.value)
                }}
              />
              <label
                htmlFor="searchInput"
                className="peer-focus:text-xs peer-focus:-top-[8px] peer-focus:text-gray-800 dark:peer-focus:text-white peer-placeholder-shown:text-lg peer-placeholder-shown:top-1"
              >
                <span className="relative">{`${t('SEARCH')} ${label}`}</span>
              </label>
              <Icon name="search-normal" className="w-5 h-5 absolute left-3 top-[.6rem]" />
            </div>
          )}

          {/* لیست گزینه‌ها */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-2 text-center text-gray-500">{t('LOADING...')}</div>
            ) : (
              options
                .filter(o => o.label.includes(filter))
                .map(option => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 py-3 border-b border-gray-50 last:border-none cursor-pointer"
                    onClick={() => handleSelect(option)}
                  >
                    <div className="grow flex items-center gap-2 font-semibold text-sm">
                      {option.icon && (
                        <img
                          src={option.icon}
                          className="w-8 h-8 rounded-full"
                          alt={option.label}
                        />
                      )}
                      {option.label}
                    </div>
                    <input
                      type="radio"
                      className="radio-primary"
                      checked={value?.value === option.value}
                      readOnly
                    />
                  </label>
                ))
            )}

            {!loading && options.filter(o => o.label.includes(filter)).length === 0 && (
              <div className="p-4 text-center text-gray-400">موردی یافت نشد</div>
            )}
          </div>
        </div>
      </BottomSheet>
    </React.Fragment>
  )
}

export default BottomSheetDropdown
